/**
 * Members-only API calls are not sent for a signed-out visitor.
 *
 * Web answers these endpoints 403 to a visitor, every time. The pages already handle that 403
 * (a locked panel, an upgrade prompt, an empty row), but each refused request also put a
 * "Failed to load resource: 403" line in the console: 26 of the URLs PageSpeed checks lost
 * Best practices points for it. So, for a visitor, the request is answered here instead, with
 * the same status and the same body web would have sent, and the page behaves exactly as it did.
 *
 * Who is a visitor is learnt from the user call (`/API/v2/user`, see `useGetUserData`). Until it
 * has answered, a browser holding a sign-in (Amplify tokens, or the app's stored user) is treated
 * as a member and its requests go out at once, so members never wait; a browser holding none
 * waits for the answer, for at most `UNKNOWN_WAIT_MS`, and then sends as before.
 *
 * Add an endpoint here only when web refuses it to every visitor; copy the body web sends.
 */
import type { AxiosAdapter, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { AxiosError, AxiosHeaders } from "axios";

export const VISITOR_EMAIL = "visitor@ortex.com";

export interface MembersOnlyRule {
    method: "get" | "post";
    /** Tested against the request path (no origin, no query). */
    path: RegExp;
    /** What web answers a visitor (`detail`, and `upgrade` where the page reads it). */
    body: Record<string, unknown>;
}

const NO_PERMISSION = { detail: "You do not have permission to perform this action." };
const IR_UPGRADE = { detail: "Index Rebalance requires an ORTEX Advanced plan.", upgrade: "advanced" };
const BACKTESTER = { detail: "You must be signed in to use the backtester." };

export const MEMBERS_ONLY: MembersOnlyRule[] = [
    // Guided-tour impression counter (the tours themselves show to visitors).
    { method: "post", path: /^\/interface\/api\/tutorials\/[^/]+\/impression\/?$/, body: { detail: "Authentication credentials were not provided." } },
    // Chart preferences: the ORTEX Charts toolbar asks on every chart page.
    { method: "get", path: /^\/interface\/api\/user\/chart-preferences\/?$/, body: { detail: "authentication required" } },
    { method: "get", path: /^\/interface\/api\/alerts\/strategies\/?$/, body: NO_PERMISSION },
    { method: "get", path: /^\/interface\/api\/backtester\/(strategies|exchanges|trading_strategy_alerts)\/?$/, body: BACKTESTER },
    { method: "get", path: /^\/interface\/api\/bonds\/(market\/panels|stock\/\d+(\/tab)?)\/?$/, body: NO_PERMISSION },
    { method: "get", path: /^\/interface\/api\/events\/earnings\/summary\/?$/, body: NO_PERMISSION },
    { method: "get", path: /^\/interface\/api\/buybacks\/(summary|stock\/\d+)\/?$/, body: NO_PERMISSION },
    { method: "get", path: /^\/interface\/api\/market_intelligence\/inbox\/?$/, body: { detail: "Sign in to manage Market Intelligence notifications." } },
    { method: "get", path: /^\/interface\/api\/index_rebal\/v2\/(stock|brief|deals|alerts\/subscriptions)\/?$/, body: IR_UPGRADE },
];

/** A request URL's path: no origin, query or hash, always with a leading slash. */
const requestPath = (url: string | undefined): string => {
    const path = (url || "")
        .split("?")[0]
        .split("#")[0]
        .replace(/^[a-z]+:\/\/[^/]+/i, "");
    return path.startsWith("/") ? path : `/${path}`;
};

/** The rule a request falls under, or null. `url` may be absolute or relative to the API base. */
export const membersOnlyRule = (method: string | undefined, url: string | undefined, rules: MembersOnlyRule[] = MEMBERS_ONLY): MembersOnlyRule | null => {
    if (!url) return null;
    const m = (method || "get").toLowerCase();
    const path = requestPath(url);
    return rules.find((r) => r.method === m && r.path.test(path)) ?? null;
};

/** A user from `/API/v2/user` (or null when that call said "not signed in") is a visitor. */
export const isVisitorUser = (user: { email?: string | null; access_level?: number | null } | null | undefined): boolean => !user || !user.email || user.email === VISITOR_EMAIL || user.access_level === -1;

type Status = "unknown" | "visitor" | "member";
let status: Status = "unknown";
let waiters: Array<() => void> = [];

/** Record what the user call said. Called with the user, or null when it answered 401/403. */
export const noteUser = (user: { email?: string | null; access_level?: number | null } | null | undefined): void => {
    status = isVisitorUser(user) ? "visitor" : "member";
    const w = waiters;
    waiters = [];
    w.forEach((f) => f());
};

/** For tests. */
export const resetVisitorGate = (): void => {
    status = "unknown";
    waiters = [];
};

export const visitorStatus = (): Status => status;

const UNKNOWN_WAIT_MS = 8000;

/** Whether this browser holds a sign-in, before the user call has said so. */
const holdsSignIn = (): boolean => {
    try {
        if (typeof window === "undefined") return true;
        const ls = window.localStorage;
        if (ls.getItem("ortexUser")) return true;
        for (let i = 0; i < ls.length; i++) {
            const k = ls.key(i) || "";
            if (k.startsWith("CognitoIdentityServiceProvider") && k.endsWith(".accessToken")) return true;
        }
        return false;
    } catch {
        return true; // storage blocked: send as before
    }
};

const settled = (timeoutMs: number): Promise<void> =>
    new Promise((resolve) => {
        if (status !== "unknown") return resolve();
        const timer = setTimeout(done, timeoutMs);
        function done() {
            clearTimeout(timer);
            waiters = waiters.filter((f) => f !== done);
            resolve();
        }
        waiters.push(done);
    });

/** The adapter that answers for web: the same status and body, through the caller's validateStatus. */
export const refusedAdapter =
    (rule: MembersOnlyRule): AxiosAdapter =>
    (config: InternalAxiosRequestConfig) => {
        const response: AxiosResponse = { data: rule.body, status: 403, statusText: "Forbidden", headers: new AxiosHeaders(), config, request: null };
        const ok = !config.validateStatus || config.validateStatus(403);
        return ok ? Promise.resolve(response) : Promise.reject(new AxiosError("Request failed with status code 403", AxiosError.ERR_BAD_REQUEST, config, null, response));
    };

/** The user call. Each answer to it says again who is signed in (a sign-in or sign-out changes it). */
const USER_CALL = /^\/API\/v2\/user\/?$/;
const isUserCall = (method: string | undefined, url: string | undefined) => (method || "get").toLowerCase() === "get" && USER_CALL.test(requestPath(url));

const installed = new WeakSet<AxiosInstance>();

/** Answer members-only requests locally for visitors on this axios instance. Idempotent. */
export const installVisitorGate = (instance: AxiosInstance, { rules = MEMBERS_ONLY, waitMs = UNKNOWN_WAIT_MS, signedInHint = holdsSignIn }: { rules?: MembersOnlyRule[]; waitMs?: number; signedInHint?: () => boolean } = {}): void => {
    if (installed.has(instance)) return;
    installed.add(instance);
    instance.interceptors.request.use(async (config) => {
        // A new answer is on its way (after a sign-in, say): until it lands, judge by the stored sign-in.
        if (isUserCall(config.method, config.url)) status = "unknown";
        const rule = membersOnlyRule(config.method, config.url, rules);
        if (!rule) return config;
        if (status === "unknown" && !signedInHint()) await settled(waitMs);
        // A sign-in made on this page (One Tap, the login form) holds tokens before the next user
        // call says so: fail open then, as for "unknown".
        if (status === "visitor" && !signedInHint()) config.adapter = refusedAdapter(rule);
        return config;
    });
    instance.interceptors.response.use(
        (res) => {
            if (isUserCall(res.config?.method, res.config?.url)) {
                if (res.status === 200 && res.data && typeof res.data === "object") noteUser(res.data);
                else if (res.status === 401 || res.status === 403) noteUser(null);
            }
            return res;
        },
        (err) => {
            const st = err?.response?.status;
            if (isUserCall(err?.config?.method, err?.config?.url) && (st === 401 || st === 403)) noteUser(null);
            return Promise.reject(err);
        }
    );
};
